// Single source of truth for the pet-insurance base coverages (Grunddeckungen) and their
// sub-coverages (Teildeckungen/SubTreatments), modeled 1:1 on the `Coverages` schema in
// openapi_pet2.json. Every consumer (step-1 checkboxes, the /recommendations and offer
// payload builders, the step-2 plan table, and the step-3 coverage editor) iterates this
// array instead of hardcoding per-coverage branches, so adding/removing a coverage later
// is a config change here, not a multi-file rewrite.

export type BaseCoverageId =
  | "disease"
  | "preexistingConditions"
  | "accident"
  | "rescue"
  | "legal"
  | "propertyDamage"
  | "emergencyCare";

export type DeductibleShape = "ageBanded" | "single" | "none";

/** Where a coverage's `requested` flag is sent: per-pet under `product.pets[].coverages`
 * (the default), or contract-wide under `product.contractCoverages` (propertyDamage,
 * emergencyCare — see `ContractCoverages` in the openapi schema). */
export type CoverageLevel = "pet" | "contract";

export interface SubCoverageDef {
  /** Matches the SubTreatment field name on the parent coverage in the API schema. */
  id: string;
  labelKey: string;
  /** true = always requested, not user-togglable (e.g. the subs of Vorerkrankung) — rendered as a static badge. */
  fixed?: boolean;
  /** API field name when it differs from `id` (disease's dental treatment is `dentalTreatmentDisease`). */
  apiKey?: string;
  /** Only available (and only paid in a claim) together with Krankheit — dental treatment and
   * preventive treatment. Backend: `clear_disease_only_sub_treatments`, `DamageCoveredService::PetV2`. */
  diseaseOnly?: boolean;
  /** Wartefrist in days when Krankheit is insured (Unfall has none) — mirrors the backend's
   * `grace_periods_september_2026` (config/pet_config.yml). Informational only. */
  graceDays?: number;
  /** Translation key of the explanation shown behind the info icon (same texts as the backend tooltips). */
  infoKey?: string;
  /** Translation key of the dialog title for `infoKey`. */
  infoTitleKey?: string;
  /** Sum insured may go up to 100'000 (openapi `SubTreatmentUnlimited`) instead of 10'000. */
  unlimited?: boolean;
  /** Explicit upper bound for the sum insured, overriding the 10'000 / 100'000 default. */
  maxSumInsured?: number;
}

export interface BaseCoverageDef {
  id: BaseCoverageId;
  labelKey: string;
  infoTitleKey: string;
  infoDescriptionKey: string;
  /** Existing repo asset path, if any (relative to public/ or src/assets/icons). */
  iconSrc?: string;
  /** This coverage can only be requested together with the referenced one (e.g. preexistingConditions -> disease). */
  requires?: BaseCoverageId;
  deductible: DeductibleShape;
  subs: SubCoverageDef[];
  /** The sub-coverages are plain on/off flags without a sum insured of their own
   * (openapi `SubTreatmentFlag`, as on preexistingConditions). */
  subsAreFlags?: boolean;
  level: CoverageLevel;
  /** Pet V2: no longer a customer-facing checkbox anywhere in the funnel — always sent as
   * `requested: true` in every /recommendations, /premium-calculations, and /offers request,
   * regardless of `coverageSelections`. */
  alwaysRequested?: boolean;
  /** An `alwaysRequested` coverage that is on by default but can be switched off on the step-3
   * customization page (rescue). Off only when `coverageSelections[id] === false`. */
  deselectable?: boolean;
  /** Pet V2: not shown at all on the step-3 customization page (legal, rescue; propertyDamage and
   * emergencyCare for now) — they are still sent as always requested. */
  hiddenInCustomization?: boolean;
  /** Never offered to the customer: not selectable anywhere, never shown in summaries and always
   * sent as `requested: false` (legal — Rechtsschutz is currently rejected by underwriting). */
  neverRequested?: boolean;
}

const dentalTreatment: SubCoverageDef = { id: "dentalTreatment", labelKey: "coverages.subs.dentalTreatment.label", infoKey: "coverages.subs.dentalTreatment.info", infoTitleKey: "coverages.subs.dentalTreatment.infoTitle" };
// Krankheit's dental treatment is `dentalTreatmentDisease` in the API (with a sum insured, up to
// unlimited); Vorerkrankung's is a plain flag called `dentalTreatment`.
const dentalTreatmentDisease: SubCoverageDef = { ...dentalTreatment, infoKey: "coverages.subs.dentalTreatment.info", infoTitleKey: "coverages.subs.dentalTreatment.infoTitle", apiKey: "dentalTreatmentDisease", unlimited: true, graceDays: 30, diseaseOnly: true };
const preventiveTreatment: SubCoverageDef = { id: "preventiveTreatment", labelKey: "coverages.subs.preventiveTreatment.label", infoKey: "coverages.subs.preventiveTreatment.info", infoTitleKey: "coverages.subs.preventiveTreatment.infoTitle", graceDays: 0, diseaseOnly: true, maxSumInsured: 1000 };
const alternativeTreatment: SubCoverageDef = { id: "alternativeTreatment", labelKey: "coverages.subs.alternativeTreatment.label", infoKey: "coverages.subs.alternativeTreatment.info", infoTitleKey: "coverages.subs.alternativeTreatment.infoTitle", graceDays: 30 };
const emergencyTreatment: SubCoverageDef = { id: "emergencyTreatment", labelKey: "coverages.subs.emergencyTreatment.label", infoKey: "coverages.subs.emergencyTreatment.info", infoTitleKey: "coverages.subs.emergencyTreatment.infoTitle" };
const emergencyTreatmentUnlimited: SubCoverageDef = { ...emergencyTreatment, unlimited: true, graceDays: 30 };
const medicalFood: SubCoverageDef = { id: "medicalFood", labelKey: "coverages.subs.medicalFood.label", infoKey: "coverages.subs.medicalFood.info", infoTitleKey: "coverages.subs.medicalFood.infoTitle", graceDays: 30, maxSumInsured: 2000 };

export const BASE_COVERAGES: BaseCoverageDef[] = [
  {
    id: "accident",
    labelKey: "coverages.accident.label",
    infoTitleKey: "coverages.accident.infoTitle",
    infoDescriptionKey: "coverages.accident.infoDescription",
    iconSrc: "icons_simpego_world.svg",
    deductible: "ageBanded",
    subs: [alternativeTreatment, emergencyTreatmentUnlimited, medicalFood],
    level: "pet",
  },
  {
    id: "disease",
    labelKey: "coverages.disease.label",
    infoTitleKey: "coverages.disease.infoTitle",
    infoDescriptionKey: "coverages.disease.infoDescription",
    iconSrc: "icons_simpego_impuls.svg",
    deductible: "ageBanded",
    subs: [dentalTreatmentDisease, preventiveTreatment, alternativeTreatment, emergencyTreatmentUnlimited, medicalFood],
    level: "pet",
  },
  {
    id: "preexistingConditions",
    labelKey: "coverages.preexistingConditions.label",
    infoTitleKey: "coverages.preexistingConditions.infoTitle",
    infoDescriptionKey: "coverages.preexistingConditions.infoDescription",
    iconSrc: "icons_simpego_checkbox.svg",
    requires: "disease",
    deductible: "ageBanded",
    // Vorerkrankung's sub-coverages are plain on/off flags (no sum of their own); the customer can switch each one off.
    subs: [dentalTreatment, alternativeTreatment, emergencyTreatment, medicalFood],
    subsAreFlags: true,
    level: "pet",
  },
  {
    id: "rescue",
    labelKey: "coverages.rescue.label",
    infoTitleKey: "coverages.rescue.infoTitle",
    infoDescriptionKey: "coverages.rescue.infoDescription",
    deductible: "none",
    subs: [],
    level: "pet",
    alwaysRequested: true,
    deselectable: true,
  },
  {
    id: "legal",
    labelKey: "coverages.legal.label",
    infoTitleKey: "coverages.legal.infoTitle",
    infoDescriptionKey: "coverages.legal.infoDescription",
    deductible: "none",
    subs: [],
    level: "pet",
    neverRequested: true,
    hiddenInCustomization: true,
  },
  {
    id: "propertyDamage",
    labelKey: "coverages.propertyDamage.label",
    infoTitleKey: "coverages.propertyDamage.infoTitle",
    infoDescriptionKey: "coverages.propertyDamage.infoDescription",
    deductible: "none",
    subs: [],
    level: "contract",
    // Schäden an Mietobjekten is no longer part of the funnel: not selectable, never shown, never requested.
    neverRequested: true,
    hiddenInCustomization: true,
  },
  {
    id: "emergencyCare",
    labelKey: "coverages.emergencyCare.label",
    infoTitleKey: "coverages.emergencyCare.infoTitle",
    infoDescriptionKey: "coverages.emergencyCare.infoDescription",
    deductible: "none",
    subs: [],
    level: "contract",
    alwaysRequested: true,
    // Requested by default, but can be switched off in "Zusatzdeckungen".
    deselectable: true,
    hiddenInCustomization: true,
  },
];

/** Krankheit and Unfall are two step-1 checkboxes but one API coverage (`diseaseAndAccident`,
 * with `diseaseRequested`/`accidentRequested` flags): sum insured, deductible and sub-treatments
 * are shared between them. UI state is still keyed per line, so every edit of a shared field
 * has to be written to both ids (see `sharedLineIds`). */
export const DISEASE_AND_ACCIDENT_LINE_IDS: BaseCoverageId[] = ["disease", "accident"];

export const sharedLineIds = (id: BaseCoverageId): BaseCoverageId[] =>
  DISEASE_AND_ACCIDENT_LINE_IDS.includes(id) ? DISEASE_AND_ACCIDENT_LINE_IDS : [id];

/** The line whose figures are sent / whose premium and underwriting result are shown for the
 * merged coverage: Krankheit when selected, otherwise Unfall. */
export const primaryLineId = (
  selections: Partial<Record<BaseCoverageId, boolean>>
): BaseCoverageId | null => (selections.disease ? "disease" : selections.accident ? "accident" : null);

/** A sum insured at this amount is shown as "unlimited" (see `formatSumInsured`). */
export const UNLIMITED_SUM_INSURED = 100000;

/** Upper bound for a coverage's own `sumInsured` (openapi: 100'000 for Krankheit und Unfall,
 * 10'000 for Vorerkrankung and Rettung). */
export const baseSumInsuredMax = (id: BaseCoverageId): number =>
  id === "disease" || id === "accident" ? UNLIMITED_SUM_INSURED : 10000;

export const subSumInsuredMax = (sub: SubCoverageDef): number =>
  sub.maxSumInsured ?? (sub.unlimited ? UNLIMITED_SUM_INSURED : 10000);

/** Starting sum insured when the customer switches a sub-coverage on that the backend had not
 * recommended (so there is no figure to carry over). Roughly the "M" tier of the backend config. */
export const DEFAULT_SUB_SUM_INSURED: Record<string, number> = {
  dentalTreatment: 3000,
  preventiveTreatment: 150,
  alternativeTreatment: 1500,
  emergencyTreatment: 3000,
  medicalFood: 500,
};

/** Krankheit's `waiverHereditaryLimits` flag is kept in `subCoverageSelections` under this key.
 * `true` means "Volldeckung bei Erbkrankheiten / Geburtsgebrechen" (no 50% limit), as in the backend. */
export const WAIVER_HEREDITARY_LIMITS_KEY = "waiverHereditaryLimits";

/** Volldeckung for the merged Krankheit-und-Unfall coverage; on unless the customer switched it off. */
export const isFullCoverRequested = (
  subSelections: Partial<Record<BaseCoverageId, Record<string, boolean>>>,
  primary: BaseCoverageId
): boolean => subSelections[primary]?.[WAIVER_HEREDITARY_LIMITS_KEY] ?? subSelections.disease?.[WAIVER_HEREDITARY_LIMITS_KEY] ?? true;

/** Whether a coverage is currently requested: always-requested ones are on unless a
 * `deselectable` one was explicitly switched off. */
export const isCoverageOn = (
  selections: Partial<Record<BaseCoverageId, boolean>>,
  def: BaseCoverageDef
): boolean =>
  def.neverRequested ? false : def.deselectable ? selections[def.id] !== false : !!def.alwaysRequested || !!selections[def.id];

export const getBaseCoverage = (id: BaseCoverageId): BaseCoverageDef =>
  BASE_COVERAGES.find((c) => c.id === id)!;

/** Coverages that don't require another coverage to be selected first — used for the
 * "at least one of these must be checked" step-1 validation rule. Contract-level add-ons
 * (propertyDamage, emergencyCare) are optional extras, not a primary coverage, so they don't
 * count toward satisfying that rule on their own. */
export const INDEPENDENT_BASE_COVERAGE_IDS: BaseCoverageId[] = BASE_COVERAGES.filter(
  (c) => !c.requires && c.level === "pet" && !c.alwaysRequested && !c.neverRequested
).map((c) => c.id);
